import React, { useState, useRef } from "react";
import {
  TextField,
  Menu,
  MenuItem,
  ListItemText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import { CampaignState } from "../service/campaigns.types";

const campaignStateLabels: Record<CampaignState, string> = {
  [CampaignState.Draft]: "Draft",
  [CampaignState.Active]: "Active",
  [CampaignState.Future]: "Upcoming",
  [CampaignState.Past]: "Past",
  [CampaignState.Archived]: "Archived",
  [CampaignState.Pending_NPD]: "Pending NPD",
  [CampaignState.Pending_GlobalQuality]: "Pending - Global Quality",
  [CampaignState.Pending_Rejected_NPD]: "Pending - Rejected NPD",
  [CampaignState.Pending_Market_Proc]: "Pending - Market Proc",
  [CampaignState.Pending_Rejected_GlobalQuality]: "Pending - Rejected Global Quality",
  [CampaignState.Pending_Confirm_Price]: "Pending - Confirm Price",
  [CampaignState.Closed_Submit_PR]: "Closed - Submit PR",
};

export const CampaignStateColumnFilter = ({
  column,
  onStateChange,
  currentState,
  allowedOrderWindows = [],
}: {
  column: any;
onStateChange?: (state: CampaignState[]) => void;
currentState?: CampaignState[];
  allowedOrderWindows?: string[];
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingSubMenuOpen, setPendingSubMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filterValue: CampaignState[] = currentState ?? column.getFilterValue() ?? [];
  
const getFilterLabel = () => {
  if (!filterValue || filterValue.length === 0) {
    return "OVERALL";
  }

  if (
    filterValue.length === allowedOrderWindows.length
  ) {
    return "OVERALL";
  }

  return campaignStateLabels[filterValue[0]] || "";
};

  const filterLabel = getFilterLabel();
  const canShow = (state: string) =>
  allowedOrderWindows.includes(state);

const hasPendingStates =
  canShow("Pending_NPD") ||
  canShow("Pending_GlobalQuality") ||
  canShow("Pending_Rejected_NPD") ||
  canShow("Pending_Market_Proc") ||
  canShow("Pending_Rejected_GlobalQuality") ||
  canShow("Pending_Confirm_Price");

  const handleArrowClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (inputRef.current) {
      setAnchorEl(inputRef.current);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubMenuAnchorEl(null);
    setPendingSubMenuOpen(false);
  };

  const handleFilterSelect = (state: CampaignState | "OVERALL") => {
    if (state === "OVERALL") {
      if (onStateChange) {
       onStateChange([]);
      } else {
       column.setFilterValue([]);
      }
    } else {
      if (onStateChange) {
       onStateChange([state]);
      } else {
        column.setFilterValue([state]);
      }
    }
    handleMenuClose();
  };

  const isFilterSelected = (state: CampaignState | "OVERALL") => {
  if (state === "OVERALL") {
    return !filterValue || filterValue.length === 0;
  }

  return filterValue.includes(state);
};

  const handlePendingClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (pendingSubMenuOpen) {
      setSubMenuAnchorEl(null);
      setPendingSubMenuOpen(false);
    } else {
      setSubMenuAnchorEl(event.currentTarget);
      setPendingSubMenuOpen(true);
    }
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchorEl(null);
    setPendingSubMenuOpen(false);
  };

  // const handlePendingStateClick = (state: CampaignState) => {
  //   if (onStateChange) {
  //     onStateChange(state);
  //   } else {
  //     column.setFilterValue(state);
  //   }
  //   handleSubMenuClose();
  //   handleMenuClose();
  // };
const handlePendingStateClick = (state: CampaignState) => {
  if (onStateChange) {
    onStateChange([state]);
  } else {
    column.setFilterValue([state]);
  }

  handleSubMenuClose();
  handleMenuClose();
};
  const menuOpen = Boolean(anchorEl);
  const isPendingSelected =
  filterValue?.includes(CampaignState.Pending_NPD) ||
  filterValue?.includes(CampaignState.Pending_GlobalQuality) ||
  filterValue?.includes(CampaignState.Pending_Rejected_NPD) ||
  filterValue?.includes(CampaignState.Pending_Market_Proc) ||
  filterValue?.includes(CampaignState.Pending_Rejected_GlobalQuality) ||
  filterValue?.includes(CampaignState.Pending_Confirm_Price);

  return (
  <>
    <TextField
      inputRef={inputRef}
      size="small"
      placeholder="Filter by State"
      value={filterLabel}
      variant="standard"
      InputProps={{
        readOnly: true,
        disableUnderline: false,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              onClick={handleArrowClick}
              sx={{ padding: "6px" }}
            >
              <ArrowDropDown fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />

    {/* Main Menu */}
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        selected={isFilterSelected("OVERALL")}
        onClick={() => handleFilterSelect("OVERALL")}
      >
        <ListItemText primary="OVERALL" />
      </MenuItem>

      {hasPendingStates && (
        <MenuItem
          onClick={handlePendingClick}
          selected={isPendingSelected}
        >
          <ListItemText primary="PENDING" />
          <ArrowRight />
        </MenuItem>
      )}

      {canShow("Draft") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Draft)}
          onClick={() => handleFilterSelect(CampaignState.Draft)}
        >
          <ListItemText primary="Draft" />
        </MenuItem>
      )}

      {canShow("Active") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Active)}
          onClick={() => handleFilterSelect(CampaignState.Active)}
        >
          <ListItemText primary="Active" />
        </MenuItem>
      )}

      {canShow("Future") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Future)}
          onClick={() => handleFilterSelect(CampaignState.Future)}
        >
          <ListItemText primary="Upcoming" />
        </MenuItem>
      )}

      {canShow("Past") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Past)}
          onClick={() => handleFilterSelect(CampaignState.Past)}
        >
          <ListItemText primary="Past" />
        </MenuItem>
      )}

      {canShow("Archived") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Archived)}
          onClick={() => handleFilterSelect(CampaignState.Archived)}
        >
          <ListItemText primary="Archived" />
        </MenuItem>
      )}

      {canShow("Closed_Submit_PR") && (
        <MenuItem
          selected={isFilterSelected(CampaignState.Closed_Submit_PR)}
          onClick={() =>
            handleFilterSelect(CampaignState.Closed_Submit_PR)
          }
        >
          <ListItemText primary="Closed - Submit PR" />
        </MenuItem>
      )}
    </Menu>

    {/* Pending Sub Menu */}
    <Menu
      anchorEl={subMenuAnchorEl}
      open={pendingSubMenuOpen}
      onClose={handleSubMenuClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      {canShow("Pending_NPD") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(CampaignState.Pending_NPD)
          }
          selected={filterValue?.includes(
            CampaignState.Pending_NPD
          )}
        >
          <ListItemText primary="Pending NPD" />
        </MenuItem>
      )}

      {canShow("Pending_GlobalQuality") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(
              CampaignState.Pending_GlobalQuality
            )
          }
          selected={filterValue?.includes(
            CampaignState.Pending_GlobalQuality
          )}
        >
          <ListItemText primary="Pending - Global Quality" />
        </MenuItem>
      )}

      {canShow("Pending_Rejected_NPD") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(
              CampaignState.Pending_Rejected_NPD
            )
          }
          selected={filterValue?.includes(
            CampaignState.Pending_Rejected_NPD
          )}
        >
          <ListItemText primary="Pending - Rejected NPD" />
        </MenuItem>
      )}

      {canShow("Pending_Market_Proc") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(
              CampaignState.Pending_Market_Proc
            )
          }
          selected={filterValue?.includes(
            CampaignState.Pending_Market_Proc
          )}
        >
          <ListItemText primary="Pending - Market Proc" />
        </MenuItem>
      )}

      {canShow("Pending_Rejected_GlobalQuality") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(
              CampaignState.Pending_Rejected_GlobalQuality
            )
          }
          selected={filterValue?.includes(
            CampaignState.Pending_Rejected_GlobalQuality
          )}
        >
          <ListItemText primary="Pending - Rejected Global Quality" />
        </MenuItem>
      )}

      {canShow("Pending_Confirm_Price") && (
        <MenuItem
          onClick={() =>
            handlePendingStateClick(
              CampaignState.Pending_Confirm_Price
            )
          }
          selected={filterValue?.includes(
            CampaignState.Pending_Confirm_Price
          )}
        >
          <ListItemText primary="Pending - Confirm Price" />
        </MenuItem>
      )}
    </Menu>
  </>
);
};

export default CampaignStateColumnFilter;
